import { Loader } from 'components/Loader';
import { fetchChatHistory, setChatVisible } from 'features/chat/chatSlice';
import { useCurrentUser } from 'features/current-user/hooks';
import useWindowDimensions from 'hooks/useWindowDimensions';
import React, { FunctionComponent, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'rootReducer';
import { AnimatePresence } from 'framer-motion';
import {
  ChatScrollWrapper,
  ChatAnimation,
  ChatInput,
  ChatProfile,
  ChatHeader,
  ChatComments,
  ChatLoginPrompt,
  ChatEmpty
} from './components';
import {
  useRecentMessages,
  useSlidePanelForCrowd,
  useEventChatWithPlayer
} from 'features/chat/chatHooks';
import { usePageVisibility } from 'hooks/usePageVisibility';
import { Crowd } from 'features/crowd/Crowd';
import { useCurrentEvent } from 'features/events/eventHooks';
import { useSocialElementsDisabled } from 'features/channel/channelHooks';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { ListenerCount } from 'components/ListenerCount';
import { HeartCount } from 'components/Hearts/HeartCount';

interface ChatProps {
  fetchHistory: boolean;
}

export const Chat: FunctionComponent<ChatProps> = ({ fetchHistory }) => {
  const isLoaded = useSelector((state: RootState) => state.chat.isLoaded);
  const isVisible = useSelector((state: RootState) => state.chat.isVisible);
  const slidePanelForCrowd = useSlidePanelForCrowd();
  const messages = useRecentMessages();
  const isPageVisible = usePageVisibility();
  const isCrowdVisible = useSelector(
    (state: RootState) => state.crowd.isCrowdVisible
  );

  const channelId = useSelector(
    (state: RootState) => state.channel.channelDetails?.ownerId
  );

  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();
  const event = useCurrentEvent();
  const eventChatWithPlayer = useEventChatWithPlayer();
  const socialElementsDisabled = useSocialElementsDisabled();
  const { isDACreatorAppVersion } = useContext(UserAgentContext);
  const isSingleDAView = isDACreatorAppVersion(
    `${process.env.REACT_APP_DA_SINGLE_VIEW_MIN_VERSION}`
  );
  const showCountsBar =
    isSingleDAView && event && (event.statsVisible || event.heartingEnabled);

  useEffect(() => {
    if (isSingleDAView) return;

    if (width >= 640) {
      dispatch(setChatVisible(isVisible));
    }
  }, [dispatch, width, isVisible, isSingleDAView]);

  useEffect(() => {
    if (channelId && fetchHistory && isPageVisible) {
      dispatch(fetchChatHistory(channelId));
    }
  }, [dispatch, channelId, fetchHistory, isPageVisible]);

  if (socialElementsDisabled) {
    return null;
  }

  if (event && !event.chatEnabled) {
    return null;
  }

  const renderInner = () => {
    if (!isLoaded || !channelId) {
      return <Loader fillComponent={true} />;
    }

    return (
      <div className="tw-flex tw-relative tw-h-full">
        <div
          className={`tw-bg-background sm:tw-shadow-glow tw-w-full tw-absolute tw-top-0 tw-bottom-0 tw-flex ${
            isCrowdVisible && width < 1024
              ? 'sm:tw-rounded-tr-2xl sm:tw-rounded-br-2xl'
              : 'sm:tw-rounded-2xl'
          }`}
        >
          {slidePanelForCrowd && <Crowd />}
          <div className="tw-flex tw-flex-col tw-h-full tw-flex-1 tw-relative tw-z-50 tw-text-label">
            <ChatHeader />
            {showCountsBar && (
              <div className="tw-flex tw-flex-none tw-space-x-10 tw-items-center tw-justify-center tw-border-line-color tw-border-solid tw-border-0 tw-border-b-[1px] [&_svg]:tw-h-[12px] [&_svg]:tw-w-[12px] tw-text-[12px] tw-text-label tw-px-4 tw-py-2">
                <ListenerCount />
                <HeartCount />
              </div>
            )}
            <ChatScrollWrapper>
              {messages.length > 0 ? (
                <ChatComments messages={messages} />
              ) : (
                <ChatEmpty />
              )}
            </ChatScrollWrapper>
            {currentUser ? (
              <ChatInput channelId={channelId} />
            ) : (
              <ChatLoginPrompt />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (eventChatWithPlayer) {
    return (
      <>
        {renderInner()}
        <ChatProfile />
      </>
    );
  }

  return (
    <AnimatePresence initial={false}>
      {isVisible && (
        <>
          <ChatAnimation>{renderInner()}</ChatAnimation>
          <ChatProfile />
        </>
      )}
    </AnimatePresence>
  );
};
